// /src/pages/Register/Register.js
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import {
  Typography,
  Box,
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Checkbox,
  FormControlLabel,
  Link,
  Snackbar,
  InputAdornment,
  IconButton,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { lightTheme, darkTheme } from '../../themes/themes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogle, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { motion } from 'framer-motion';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import './register.css';
import logo from '../../Components/NavComponents/logo.png';
import axios from 'axios';
import BASE_URL from '../../API/config';

// CSS for transparent logo background and hollow effect for "1" and "X"
const styles = `
  .logo-background::before {
    content: '';
    position: absolute;
    top: -10%;
    right: -10%;
    bottom: 0;
    left: 0;
    background-size: 260px auto;
    background-position: top right;
    background-image: var(--bg-url);
    background-repeat: no-repeat;
    opacity: 0.12;
    z-index: 0;
    transform: translate(6%, -4%);
  }

  .logo-background {
    position: relative;
    overflow: hidden;
  }

  .logo-background > * {
    position: relative;
    z-index: 1;
  }

  .text-style {
    position: relative;
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    font-weight: 700;
    letter-spacing: 0.6px;
    font-size: 32px;
    color: #F3BA2F;
  }

  .text-style > span.hollow {
    -webkit-text-fill-color: transparent;
    text-fill-color: transparent;
    -webkit-text-stroke: 1.6px #F3BA2F;
    text-stroke: 1.6px #F3BA2F;
    font-size: 36px;
  }

  .text-style > span.solid {
    -webkit-text-fill-color: #ffffff;
    text-fill-color: #ffffff;
  }
`;

const Register = () => {
  const isLightTheme = useSelector((state) => state.theme.light);
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [tempUserId, setTempUserId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email) => {
    const gmailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    return gmailRegex.test(email);
  };

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateUsername = (username) => {
    return username.length >= 3;
  };

  const handleManualRegister = async (e) => {
    e.preventDefault();

    if (!username || !email || !password || !confirmPassword) {
      setError('Please fill all required fields');
      return;
    }

    if (!validateUsername(username)) {
      setError('Username must be at least 3 characters long');
      return;
    }

    if (!validateEmail(email)) {
      setError('Email must be a valid Gmail address');
      return;
    }

    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters and include a number and a special character');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!agreed) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/auth/register`, {
        username,
        email,
        password,
        referralCode: referralCode || undefined,
      });
      setTempUserId(response.data.tempUserId);
      setError('');
      setSnackbarMessage('OTP has been sent successfully!');
      setSnackbarOpen(true);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/auth/verify-otp`, {
        tempUserId,
        otp,
      });
      localStorage.setItem('token', response.data.token);
      setSnackbarMessage('Registration completed successfully! Redirecting to home...');
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/login');
        setStep('form');
        setUsername('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setReferralCode('');
        setOtp('');
        setAgreed(false);
        setTempUserId(null);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    alert('Sign Up with Google clicked! (OAuth not implemented yet)');
  };

  const handleXSignUp = () => {
    alert('Sign Up with X clicked! (OAuth not implemented yet)');
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <ThemeProvider theme={isLightTheme ? lightTheme : darkTheme}>
      <style>{styles}</style>
      <Box className="register-container">
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            className="register-grid"
            alignItems="stretch"
          >
            {/* Left side – Hero / Brand section */}
            <Grid item xs={12} md={6} className="register-left">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Box className="register-hero-card">
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" className="text-style">
                      <span className="hollow">1</span>
                      <span className="solid">Crypto</span>
                      <span className="hollow">X</span>
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{ mt: 1.5, color: 'rgba(248,250,252,0.82)' }}
                    >
                      Create your global trading account in minutes.
                    </Typography>
                  </Box>

                  <Stack spacing={2.5} sx={{ mt: 2 }}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'rgba(243,186,47,0.92)', mb: 0.5 }}
                      >
                        Institutional-grade security
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.8)' }}>
                        Your assets are protected with multi-layer security, monitoring,
                        and strict verification flows.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'rgba(243,186,47,0.92)', mb: 0.5 }}
                      >
                        Built for serious traders
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.8)' }}>
                        Spot, futures, and AI-powered strategies coming together in one
                        unified experience.
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: 'rgba(243,186,47,0.92)', mb: 0.5 }}
                      >
                        Referral rewards
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.8)' }}>
                        Share your referral code and unlock exclusive rewards as your
                        network grows.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ mt: 4 }}>
                    <Chip
                      label="New to 1CryptoX? Start your journey here."
                      className="register-chip"
                    />
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            {/* Right side – Form / OTP card */}
            <Grid item xs={12} md={6} className="register-right">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Card
                  className="logo-background register-card"
                  style={{ '--bg-url': `url(${logo})` }}
                  sx={{
                    borderRadius: 3,
                    backdropFilter: 'blur(18px)',
                    background:
                      'linear-gradient(135deg, rgba(15,23,42,0.96), rgba(15,23,42,0.98))',
                    border: '1px solid rgba(148,163,184,0.22)',
                    boxShadow:
                      '0 20px 50px rgba(0,0,0,0.75), 0 0 0 1px rgba(30,64,175,0.25)',
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    {step === 'form' ? (
                      <>
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                          <Typography
                            variant="h5"
                            sx={{
                              color: '#F3BA2F',
                              fontWeight: 700,
                              letterSpacing: 0.6,
                              mb: 0.5,
                            }}
                          >
                            Create your account
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: 'rgba(203,213,225,0.9)' }}
                          >
                            Sign up with your Gmail and start trading with 1CryptoX.
                          </Typography>
                        </Box>

                        <form onSubmit={handleManualRegister}>
                          <TextField
                            label="Username"
                            type="text"
                            fullWidth
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            sx={{ mb: 2 }}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Email (Gmail only)"
                            type="email"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            helperText="Use a valid Gmail address for verification."
                          />
                          <TextField
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={togglePasswordVisibility}
                                    edge="end"
                                  >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            label="Confirm Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={toggleConfirmPasswordVisibility}
                                    edge="end"
                                  >
                                    {showConfirmPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            label="Referral Code (Optional)"
                            type="text"
                            fullWidth
                            value={referralCode}
                            onChange={(e) => setReferralCode(e.target.value)}
                            sx={{ mb: 2.5 }}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                          />

                          <Divider
                            sx={{
                              mb: 2.5,
                              bgcolor: 'rgba(148,163,184,0.35)',
                              '&::before, &::after': {
                                borderColor: 'rgba(148,163,184,0.35)',
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgba(148,163,184,0.95)' }}
                            >
                              Or continue with
                            </Typography>
                          </Divider>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              gap: 1.5,
                              mb: 2,
                            }}
                          >
                            <Button
                              onClick={handleGoogleSignUp}
                              variant="outlined"
                              fullWidth
                              sx={{
                                borderRadius: 2,
                                py: 1.2,
                                borderColor: 'rgba(156,163,175,0.7)',
                                color: '#E5E7EB',
                                textTransform: 'none',
                                fontSize: 14,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                '&:hover': {
                                  borderColor: '#F3BA2F',
                                  backgroundColor: 'rgba(15,23,42,0.9)',
                                },
                              }}
                            >
                              <FontAwesomeIcon icon={faGoogle} size="lg" />
                              <span>Google</span>
                            </Button>
                            <Button
                              onClick={handleXSignUp}
                              variant="outlined"
                              fullWidth
                              sx={{
                                borderRadius: 2,
                                py: 1.2,
                                borderColor: 'rgba(156,163,175,0.7)',
                                color: '#E5E7EB',
                                textTransform: 'none',
                                fontSize: 14,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                '&:hover': {
                                  borderColor: '#F3BA2F',
                                  backgroundColor: 'rgba(15,23,42,0.92)',
                                },
                              }}
                            >
                              <FontAwesomeIcon icon={faXTwitter} size="lg" />
                              <span>Continue with X</span>
                            </Button>
                          </Box>

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                                sx={{ color: 'rgba(148,163,184,0.9)' }}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(148,163,184,0.95)' }}
                              >
                                I agree to the{' '}
                                <Link href="/terms" underline="hover" sx={{ color: '#F3BA2F' }}>
                                  Terms of Service
                                </Link>{' '}
                                and{' '}
                                <Link
                                  href="/privacy-policy"
                                  underline="hover"
                                  sx={{ color: '#F3BA2F' }}
                                >
                                  Privacy Policy
                                </Link>
                              </Typography>
                            }
                            sx={{ mb: 2 }}
                          />

                          {error && (
                            <Typography
                              color="error"
                              sx={{ mb: 2, textAlign: 'center', fontSize: 13 }}
                            >
                              {error}
                            </Typography>
                          )}

                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={isLoading}
                            sx={{
                              bgcolor: '#F0B90B',
                              color: '#111827',
                              fontWeight: 600,
                              borderRadius: 2,
                              py: 1.4,
                              fontSize: 15,
                              boxShadow:
                                '0 10px 26px rgba(240,185,11,0.5), 0 0 0 1px rgba(250,204,21,0.45)',
                              '&:hover': {
                                bgcolor: '#DAA520',
                                boxShadow:
                                  '0 12px 30px rgba(240,185,11,0.65), 0 0 0 1px rgba(250,204,21,0.6)',
                              },
                            }}
                          >
                            {isLoading ? 'Creating account...' : 'Register'}
                          </Button>
                        </form>

                        <Typography
                          sx={{
                            mt: 2.5,
                            textAlign: 'center',
                            color: 'rgba(148,163,184,0.95)',
                            fontSize: 14,
                          }}
                        >
                          Already have an account?{' '}
                          <Link
                            href="/login"
                            sx={{
                              color: '#F3BA2F',
                              fontWeight: 500,
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            Login
                          </Link>
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                          <Typography
                            variant="h5"
                            sx={{
                              color: '#F3BA2F',
                              fontWeight: 700,
                              letterSpacing: 0.6,
                              mb: 1,
                            }}
                          >
                            Verify your email
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: 'rgba(203,213,225,0.95)' }}
                          >
                            We’ve sent a one-time password (OTP) to {email}. Enter it below
                            to activate your account.
                          </Typography>
                        </Box>

                        <form onSubmit={handleOtpSubmit}>
                          <TextField
                            label="Enter OTP"
                            fullWidth
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            sx={{ mb: 2.5 }}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                          />

                          {error && (
                            <Typography
                              color="error"
                              sx={{ mb: 2, textAlign: 'center', fontSize: 13 }}
                            >
                              {error}
                            </Typography>
                          )}

                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={isLoading}
                            sx={{
                              bgcolor: '#F0B90B',
                              color: '#111827',
                              fontWeight: 600,
                              borderRadius: 2,
                              py: 1.4,
                              fontSize: 15,
                              boxShadow:
                                '0 10px 26px rgba(240,185,11,0.5), 0 0 0 1px rgba(250,204,21,0.45)',
                              '&:hover': {
                                bgcolor: '#DAA520',
                                boxShadow:
                                  '0 12px 30px rgba(240,185,11,0.65), 0 0 0 1px rgba(250,204,21,0.6)',
                              },
                            }}
                          >
                            {isLoading ? 'Verifying...' : 'Verify OTP'}
                          </Button>
                        </form>

                        <Button
                          onClick={() => setStep('form')}
                          variant="text"
                          sx={{
                            mt: 2.5,
                            display: 'block',
                            mx: 'auto',
                            color: '#F3BA2F',
                            textTransform: 'none',
                            fontSize: 14,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          Back to Register
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default Register;
